<template>
  <template v-if="isVisible">
    <WizardModal
      v-model="currentStepIndex"
      v-model:show-modal="showModal"
      :steps="wizardSteps"
      :modal-title="editData ? t('Editar Mensagem Agendada') : t('Agendar Mensagens em Massa')"
      size="modalMd"
      custom-buttons
      :back-label="backButtonLabel"
      :back-icon="backButtonIcon"
      :next-label="nextButtonLabel"
      :next-icon="nextButtonIcon"
      @finish="scheduleMessages"
      @cancel="handleCancel"
      @close="handleModalClose"
    >
    <!-- Step 0: Write Message -->
    <template #step-0>
        <div class="step-content" data-testid="mensagens-programadas-step-write">
          <!-- Empty State: No template selected -->
          <div v-if="!selectedTemplate" class="empty-state">
            <box class="empty-state-card">
              <div class="empty-state-content">
                <i class="pi pi-file-edit empty-state-icon"></i>
                <h3 class="empty-state-title">{{ t('Selecione um Template de Mensagem') }}</h3>
                <p class="empty-state-description">
                  {{ t('Escolha um template para enviar mensagens personalizadas para seus clientes.') }}
                </p>
                <Button
                  :label="t('Escolher Template')"
                  icon="pi pi-search"
                  size="large"
                  data-testid="mensagens-programadas-choose-template-btn"
                  @click="openTemplateSelection"
                  class="empty-state-button"
                />
              </div>
            </box>
          </div>

          <!-- Selected State: Template selected -->
          <div v-else class="template-selected">
            <!-- Template Header -->
            <div class="template-header">
              <div class="template-header-row">
                <Tag severity="success" icon="pi pi-check-circle" class="success-tag">
                  <span class="badge-text">{{ t('Template Selecionado') }}</span>
                </Tag>
                <Button
                  :label="t('Trocar Template')"
                  icon="pi pi-refresh"
                  size="small"
                  outlined
                  data-testid="mensagens-programadas-change-template-btn"
                  @click="openTemplateSelection"
                  class="change-template-btn"
                />
              </div>
              <div class="template-name-row">
                <h3 class="template-name">{{ selectedTemplate.template_name }}</h3>
              </div>
            </div>

            <!-- Message Preview Card -->
            <box :title="t('Pré-visualização da Mensagem')" icon="pi pi-comment" class="message-preview-card">
              <div class="preview-container">
                <div class="whatsapp-mockup">
                  <div class="mockup-header">
                    <div class="mockup-avatar">
                      <i class="pi pi-building"></i>
                    </div>
                    <div class="mockup-info">
                      <div class="mockup-name">{{ t('Sua Franquia') }}</div>
                      <div class="mockup-status">{{ t('online') }}</div>
                    </div>
                  </div>
                  <div class="mockup-chat">
                    <div class="whatsapp-bubble">
                      <div class="bubble-content" v-html="sanitizeHtmlContent(selectedTemplateContent)"></div>
                      <div class="bubble-time">{{ new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) }}</div>
                    </div>
                  </div>
                </div>
                <div class="message-info">
                  <i class="pi pi-users"></i>
                  <span>{{ t('Esta mensagem será enviada para') }} <strong>{{ selectedClientes.length }}</strong> {{ t('cliente(s)') }}</span>
                </div>
              </div>
            </box>

            <!-- Variables Help (Collapsible) -->
            <Accordion class="variables-accordion">
              <AccordionTab>
                <template #header>
                  <div class="accordion-header">
                    <i class="pi pi-info-circle"></i>
                    <span>{{ t('Variáveis de Template') }}</span>
                  </div>
                </template>
                <div class="variables-content">
                  <p class="variables-intro">{{ t('Você pode usar variáveis na sua mensagem que serão substituídas pelos dados do cliente:') }}</p>
                  <div class="variables-grid">
                    <div class="variables-column">
                      <strong>{{ t('Variáveis disponíveis:') }}</strong>
                      <ul class="variables-list">
                        <li><code>{customer_name}</code> - {{ t('Nome do cliente') }}</li>
                        <li><code>{city}</code> - {{ t('Cidade do cliente') }}</li>
                        <li><code>{neighborhood}</code> - {{ t('Bairro do cliente') }}</li>
                        <li><code>{franchise}</code> - {{ t('Franquia') }}</li>
                      </ul>
                    </div>
                    <div class="variables-column">
                      <strong>{{ t('Exemplo:') }}</strong>
                      <p class="example-text"><em>"{{ t('Olá') }} {customer_name}, {{ t('esperamos que esteja bem em') }} {city}!"</em></p>
                      <p class="example-result"><small>{{ t('Será substituído por')}}: "{{ t('Olá') }} João Silva, {{ t('esperamos que esteja bem em') }} São Paulo!"</small></p>
                    </div>
                  </div>
                </div>
              </AccordionTab>
            </Accordion>
          </div>
        </div>
    </template>

    <!-- Step 1: Review Information -->
    <template #step-1>
        <div class="step-content review-step" data-testid="mensagens-programadas-step-review">
          <!-- Info Banner -->
          <div class="info-banner">
            <div class="info-banner-icon">
              <i class="pi pi-clock"></i>
            </div>
            <div class="info-banner-content">
              <h4>{{ t('Programação Automática') }}</h4>
              <p>{{ t('As mensagens serão agendadas automaticamente com intervalos de 3-8 segundos entre elas.') }}</p>
            </div>
          </div>

          <!-- Review Table -->
          <data-table
            :config="reviewTableConfig"
            :data="reviewData"
          />

          <!-- Message Modal -->
          <Modal
            :show="showMessageModal"
            :title="t('Mensagem Completa')"
            modal-size="modal-sm"
            :show-save-button="false"
            :close-label="t('Fechar')"
            @close="showMessageModal = false"
          >
            <div class="full-message-content">
              {{ currentFullMessage }}
            </div>
          </Modal>
        </div>
    </template>

    <!-- Step 2: Schedule Messages -->
    <template #step-2>
        <div class="step-content" data-testid="mensagens-programadas-step-confirm">
          <Message severity="warn" :closable="false" icon="pi pi-clock">
            <strong v-if="editData">{{ t('Confirmação:') }}</strong>
            <strong v-else>{{ t('Confirmação:') }}</strong>
            <span v-if="editData"> {{ t('Você está prestes a editar esta mensagem agendada.') }}</span>
            <span v-else> {{ t('Você está prestes a agendar {count} mensagem(s).', { count: reviewData.length }) }}
            {{ t('As mensagens serão enviadas automaticamente nos horários agendados.') }}</span>
          </Message>

          <div v-if="isScheduling" class="loading-container">
            <i class="pi pi-spinner pi-spin"></i>
            <span v-if="editData">{{ t('Salvando alterações...') }}</span>
            <span v-else>{{ t('Agendando mensagens...') }}</span>
          </div>
        </div>
    </template>
    </WizardModal>
  </template>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { sanitizeHtmlContent } from '@/utils/sanitizeHtmlContent';
import WizardModal from '@/ui/WizardModal.vue';
import Modal from '@/ui/Modal.vue';
import { useMensagensProgramadas } from '@/composables/useMensagensProgramadas';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';
import type { MensagensProgramadas_DeviceRow } from '@/contracts/MensagensProgramadas_DeviceRow';

type WhatsAppDevice = MensagensProgramadas_DeviceRow;

interface SelectedCliente {
  cliente_id: number | string;
  cliente_nome?: string;
  cliente_franquia_id?: number | string;
  franquia?: string;
  localidade?: string;
  cliente_bairro?: string;
  cliente_telefone?: string;
  cliente_ddd?: string;
  cliente_ddi?: string;
  [key: string]: unknown;
}

interface MessageTemplate {
  template_id: number | string;
  template_name: string;
  template_content: string;
  [key: string]: unknown;
}

interface ReviewClienteSnapshot {
  cliente_id: number | string;
  cliente_nome?: string;
  franquia_id?: number | string;
  franquia?: string;
  localidade?: string;
  cliente_telefone?: string;
  cliente_ddd?: string;
  cliente_ddi?: string;
}

interface ReviewRow {
  cliente: ReviewClienteSnapshot;
  processedMessage: string;
  selectedDevice: number | string | null;
}

interface CellParams {
  data: ReviewRow & { _isFrozenFooter?: boolean };
  value?: unknown;
}

interface EditData {
  message_id: number | string;
  content?: string;
  meta?: string | { template_id?: number | string; template_name?: string };
  [key: string]: unknown;
}

interface OpenPayload {
  selectedClientes?: SelectedCliente[];
  editData?: EditData;
}

const { t } = useI18n();

// State - visibility controlled by eventBus
const isVisible = ref(false);
const selectedClientes = ref<SelectedCliente[]>([]);

// Composables
const { fetchWhatsAppDevices, sendMessages, editScheduledMessage } = useMensagensProgramadas();

// Data
const showModal = ref(false);
const currentStepIndex = ref(0); // WizardModal uses 0-based indexing
const selectedTemplate = ref<MessageTemplate | null>(null);
const selectedTemplateContent = ref('');
const reviewData = ref<ReviewRow[]>([]);
const whatsappDevices = ref<WhatsAppDevice[]>([]);
const formattedDevices = ref<Record<string, WhatsAppDevice[]>>({});
const isScheduling = ref(false);
const showMessageModal = ref(false);
const currentFullMessage = ref('');
const editData = ref<EditData | null>(null); // Holds row data when in edit mode

// Wizard steps configuration with validation
const wizardSteps = computed(() => [
  {
    title: t('Escrever Mensagem'),
    validate: (): boolean => selectedTemplate.value !== null
  },
  {
    title: t('Revisar Informações'),
    validate: (): boolean => reviewData.value.length > 0 && reviewData.value.every(item => item.selectedDevice),
    onEnter: (): void => {
      // Prepare review data when entering this step
      prepareReviewData();
    }
  },
  {
    title: t('Agendar'),
    validate: (): boolean => true
  }
]);

// Button labels for WizardModal
const backButtonLabel = computed(() => {
  return (step: number): string => {
    if (step === 0) {
      return t('Fechar');
    }
    return t('Voltar');
  };
});

const backButtonIcon = computed(() => {
  return (step: number): string => {
    if (step === 0) {
      return 'pi pi-times';
    }
    return 'pi pi-arrow-left';
  };
});

const nextButtonLabel = computed(() => {
  return (step: number): string => {
    if (step === 0 || step === 1) {
      return t('Próximo');
    } else if (step === 2) {
      if (editData.value) {
        return isScheduling.value ? t('Salvando...') : t('Salvar Alterações');
      }
      return isScheduling.value ? t('Agendando...') : t('Agendar Mensagens');
    }
    return t('Próximo');
  };
});

const nextButtonIcon = computed(() => {
  return (step: number): string => {
    if (step === 0 || step === 1) {
      return 'pi pi-arrow-right';
    } else if (step === 2) {
      return editData.value ? 'pi pi-save' : 'pi pi-calendar-plus';
    }
    return 'pi pi-arrow-right';
  };
});

const escapeHtml = (str: string): string => {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
};

const truncate = (str: string, max: number): string => {
  if (!str) return '';
  return str.length > max ? str.substring(0, max) + '...' : str;
};

// Review DataTable config (Step 1)
const reviewTableConfig = computed(() => ({
  tableId: 'mensagensProgramadasReviewTable',
  pagination: false,
  columns: [
    {
      data: 'cliente.cliente_nome',
      title: t('Cliente'),
      sortable: true,
      cellRenderer: (params: CellParams) => {
        const nome = escapeHtml(params.data.cliente?.cliente_nome || '');
        return `<div class="client-cell"><i class="pi pi-user client-icon"></i><span>${nome}</span></div>`;
      }
    },
    {
      data: 'cliente.franquia',
      title: t('Franquia'),
      sortable: true,
      cellRenderer: (params: CellParams) => {
        const franquia = escapeHtml(params.data.cliente?.franquia || '');
        return `<span class="p-tag p-tag-info"><span class="p-tag-value">${franquia}</span></span>`;
      }
    },
    {
      data: 'selectedDevice',
      title: t('Dispositivo WhatsApp'),
      width: 250,
      cellRenderer: (params: CellParams) => renderDeviceSelect(params)
    },
    {
      data: 'processedMessage',
      title: t('Mensagem'),
      width: 200,
      cellRenderer: (params: CellParams) => {
        const full = params.data.processedMessage || '';
        const truncated = escapeHtml(truncate(full, 50));
        const container = document.createElement('div');
        container.className = 'message-cell';
        container.setAttribute('data-testid', 'mensagens-programadas-view-message');
        container.innerHTML = `
          <span class="message-truncated">${truncated}</span>
          <button class="p-button p-button-text p-button-rounded p-button-sm view-message-btn" type="button" data-testid="mensagens-programadas-view-message-btn">
            <i class="pi pi-eye"></i>
          </button>
        `;
        container.addEventListener('click', () => openMessageModal(full));
        return container;
      }
    }
  ]
}));

const renderDeviceSelect = (params: CellParams): HTMLElement => {
  const row = params.data;
  const devices = getDevicesForFranquia(row.cliente?.franquia_id);

  const select = document.createElement('select');
  select.className = 'p-inputtext p-component w-full mensagens-device-select';
  select.setAttribute('data-testid', 'mensagens-programadas-device-select');
  select.disabled = isScheduling.value;

  const placeholder = document.createElement('option');
  placeholder.value = '';
  placeholder.textContent = t('Selecione um dispositivo');
  placeholder.disabled = true;
  if (row.selectedDevice == null || row.selectedDevice === '') {
    placeholder.selected = true;
  }
  select.appendChild(placeholder);

  devices.forEach((device) => {
    const opt = document.createElement('option');
    opt.value = String(device.id);
    opt.textContent = device.display_name;
    if (row.selectedDevice != null && String(row.selectedDevice) === String(device.id)) {
      opt.selected = true;
    }
    select.appendChild(opt);
  });

  select.addEventListener('change', (event) => {
    const target = event.target as HTMLSelectElement;
    const newValue = target.value;
    const matched = devices.find(d => String(d.id) === newValue);
    row.selectedDevice = matched ? matched.id : newValue;
  });

  return select;
};

// Methods
const handleCancel = (): void => {
  log('MensagensProgramadasWizard', 'handleCancel', 'Cancel wizard');

  // If scheduling, don't allow canceling
  if (isScheduling.value) {
    return;
  }

  showModal.value = false;
  isVisible.value = false;
  resetWizard();
  selectedClientes.value = [];
};

const handleModalClose = (): void => {
  log('MensagensProgramadasWizard', 'handleModalClose', 'Modal close event');

  // If scheduling, don't allow closing
  if (isScheduling.value) {
    return;
  }

  handleCancel();
};

const handleClose = (): void => {
  if (isScheduling.value) {
    return; // Don't allow closing while scheduling
  }
  log('MensagensProgramadasWizard', 'handleClose', 'Closing modal');
  showModal.value = false;
  isVisible.value = false;
  resetWizard();
  selectedClientes.value = [];
};

const resetWizard = (): void => {
  currentStepIndex.value = 0; // Reset to first step (0-indexed)
  selectedTemplate.value = null;
  selectedTemplateContent.value = '';
  reviewData.value = [];
  isScheduling.value = false;
  editData.value = null;
};

const openTemplateSelection = (): void => {
  log('MensagensProgramadasWizard', 'openTemplateSelection', 'Opening template selection');

  // Extract unique franchise IDs from selected clients
  const franchiseIds = [...new Set(selectedClientes.value.map(cliente => cliente.cliente_franquia_id))];

  log('MensagensProgramadasWizard', 'openTemplateSelection', 'Franchise IDs', franchiseIds);

  // Emit event for template selection modal
  eventBus.emit('template.open-selection', {
    franchiseIds,
    callback: handleTemplateSelected
  });
};

const handleTemplateSelected = (template: MessageTemplate): void => {
  log('MensagensProgramadasWizard', 'handleTemplateSelected', 'Template selected', template);
  selectedTemplate.value = template;
  selectedTemplateContent.value = template.template_content;
};

const loadWhatsAppDevices = async (): Promise<void> => {
  try {
    const devices = await fetchWhatsAppDevices();
    whatsappDevices.value = devices;

    // Group devices by franchise ID
    const devicesByFranquia: Record<string, WhatsAppDevice[]> = {};
    devices.forEach(device => {
      // Extract franchise ID from device (adjust based on your data structure)
      // For now, we'll group all devices together
      const franquiaId = 'all'; // This should be device.franquia_id if available
      if (!devicesByFranquia[franquiaId]) {
        devicesByFranquia[franquiaId] = [];
      }
      devicesByFranquia[franquiaId].push(device);
    });

    formattedDevices.value = devicesByFranquia;
    log('MensagensProgramadasWizard', 'loadWhatsAppDevices', 'Loaded WhatsApp devices', devices);
  } catch (error) {
    log('MensagensProgramadasWizard', 'loadWhatsAppDevices', 'Error loading WhatsApp devices', error);
  }
};

const getDevicesForFranquia = (franquiaId: number | string | undefined): WhatsAppDevice[] => {
  const key = franquiaId == null ? '' : String(franquiaId);
  return formattedDevices.value[key] || formattedDevices.value['all'] || whatsappDevices.value;
};

const prepareReviewData = (): void => {
  log('MensagensProgramadasWizard', 'prepareReviewData', 'Preparing review data from selected clientes', selectedClientes.value);

  // Map selected clientes to review data format
  reviewData.value = selectedClientes.value.map((cliente): ReviewRow => {
    // Process template variables
    const processedMessage = selectedTemplateContent.value
      .replace(/{customer_name}/g, cliente.cliente_nome || '')
      .replace(/{city}/g, cliente.localidade || '')
      .replace(/{neighborhood}/g, cliente.cliente_bairro || '')
      .replace(/{franchise}/g, cliente.franquia || '');

    // Get devices for this franchise
    const availableDevices = getDevicesForFranquia(cliente.cliente_franquia_id);

    // Auto-select first device if available
    const autoSelectedDevice = availableDevices.length > 0 ? availableDevices[0].id : null;

    return {
      cliente: {
        cliente_id: cliente.cliente_id,
        cliente_nome: cliente.cliente_nome,
        franquia_id: cliente.cliente_franquia_id,
        franquia: cliente.franquia,
        localidade: cliente.localidade,
        cliente_telefone: cliente.cliente_telefone,
        cliente_ddd: cliente.cliente_ddd,
        cliente_ddi: cliente.cliente_ddi
      },
      processedMessage: processedMessage,
      selectedDevice: autoSelectedDevice // Auto-select first device
    };
  });

  log('MensagensProgramadasWizard', 'prepareReviewData', 'Review data prepared', reviewData.value.length, 'items');
};

const openMessageModal = (message: string): void => {
  log('MensagensProgramadasWizard', 'openMessageModal', 'Opening message modal');
  currentFullMessage.value = message;
  showMessageModal.value = true;
};

const scheduleMessages = async (): Promise<void> => {
  log('MensagensProgramadasWizard', 'scheduleMessages', 'Scheduling messages', { isEditMode: !!editData.value });

  // Edit mode: update existing scheduled message
  if (editData.value) {
    if (!selectedTemplate.value) {
      eventBus.emit('globalMessage.warn', t('Nenhum template selecionado'));
      return;
    }

    isScheduling.value = true;

    try {
      const payload: Record<string, unknown> = {
        template_id: selectedTemplate.value.template_id
      };

      // Include device selection if available from reviewData
      if (reviewData.value.length > 0 && reviewData.value[0].selectedDevice) {
        payload.whatsappfranquia_id = reviewData.value[0].selectedDevice;
      }

      const result = await editScheduledMessage(editData.value.message_id, payload);

      log('MensagensProgramadasWizard', 'scheduleMessages', 'Message edited successfully', result);

      eventBus.emit('globalMessage.success', t('Mensagem agendada atualizada com sucesso!'));

      // Close modal and refresh table
      handleClose();
      eventBus.emit('mensagens-programadas.refresh');

    } catch (error) {
      const err = error as Error;
      log('MensagensProgramadasWizard', 'scheduleMessages', 'Error editing scheduled message', err);
      eventBus.emit('globalMessage.error', t('Erro ao editar mensagem agendada') + ': ' + err.message);
    } finally {
      isScheduling.value = false;
    }
    return;
  }

  // Create mode: schedule new messages
  if (!selectedTemplate.value) {
    eventBus.emit('globalMessage.warn', t('Nenhum template selecionado'));
    return;
  }

  if (reviewData.value.length === 0) {
    eventBus.emit('globalMessage.warn', t('Nenhum cliente selecionado'));
    return;
  }

  // Validate that all customers have devices selected
  const missingDevices = reviewData.value.filter(item => !item.selectedDevice);
  if (missingDevices.length > 0) {
    eventBus.emit('globalMessage.warn', t('Por favor, selecione um dispositivo WhatsApp para todos os clientes.'));
    return;
  }

  isScheduling.value = true;

  try {
    const templateId = selectedTemplate.value!.template_id;
    // Prepare messages array
    const messages = reviewData.value.map(item => ({
      cliente_id: item.cliente.cliente_id,
      template_id: templateId,
      whatsappfranquia_id: item.selectedDevice,
      franquia_id: item.cliente.franquia_id
    }));

    const currentDatetime = new Date().toISOString();

    const result = await sendMessages(messages, currentDatetime);

    log('MensagensProgramadasWizard', 'scheduleMessages', 'Messages scheduled successfully', result);

    if (!result) {
      throw new Error(t('Resposta vazia do servidor ao agendar mensagens'));
    }

    eventBus.emit('globalMessage.success', t('{successCount} mensagem(s) agendada(s) com sucesso! {errorCount} erro(s).', {
      successCount: result.success_count,
      errorCount: result.error_count
    }));

    // Close modal and refresh table
    handleClose();
    eventBus.emit('mensagens-programadas.refresh');

  } catch (error) {
    const err = error as Error;
    log('MensagensProgramadasWizard', 'scheduleMessages', 'Error scheduling messages', err);
    eventBus.emit('globalMessage.error', t('Erro ao agendar mensagens') + ': ' + err.message);
  } finally {
    isScheduling.value = false;
  }
};

// EventBus handlers
const handleOpen = (data?: OpenPayload): void => {
  log('MensagensProgramadasWizard', 'handleOpen', 'Opening wizard via eventBus', data);
  selectedClientes.value = data?.selectedClientes || [];
  isVisible.value = true;
  showModal.value = true;
  resetWizard();

  // Edit mode: pre-fill from existing row data
  if (data?.editData) {
    editData.value = data.editData;
    log('MensagensProgramadasWizard', 'handleOpen', 'Edit mode activated', data.editData);

    // Pre-fill template if meta contains template info
    if (data.editData.meta) {
      const meta = typeof data.editData.meta === 'string'
        ? (() => {
            try {
              return JSON.parse(data.editData.meta as string) as { template_id?: number | string; template_name?: string };
            } catch (_e) {
              return {} as { template_id?: number | string; template_name?: string };
            }
          })()
        : data.editData.meta;

      if (meta.template_id || meta.template_name) {
        selectedTemplate.value = {
          template_id: meta.template_id ?? '',
          template_name: meta.template_name || '',
          template_content: data.editData.content || ''
        };
        selectedTemplateContent.value = data.editData.content || '';
      }
    }
  }

  loadWhatsAppDevices();
};

// Lifecycle
onMounted(() => {
  log('MensagensProgramadasWizard', 'onMounted', 'Wizard mounted, listening for events');
  eventBus.on('modal.mensagens-programadas-wizard.open', handleOpen);
});

onBeforeUnmount(() => {
  log('MensagensProgramadasWizard', 'onBeforeUnmount', 'Cleaning up event listeners');
  eventBus.off('modal.mensagens-programadas-wizard.open', handleOpen);
});
</script>

<style scoped>
/* Step Content */
.step-content {
  min-height: 400px;
  padding: 1.5rem;
}

/* Empty State */
.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 350px;
}

.empty-state-card {
  max-width: 500px;
  width: 100%;
}

.empty-state-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 2rem 1rem;
}

.empty-state-icon {
  font-size: 4rem;
  color: var(--primary-color);
  margin-bottom: 1.5rem;
  opacity: 0.7;
}

.empty-state-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color);
  margin: 0 0 0.75rem 0;
}

.empty-state-description {
  font-size: 1rem;
  color: var(--text-color-secondary);
  margin: 0 0 2rem 0;
  line-height: 1.5;
}

.empty-state-button {
  min-width: 200px;
}

/* Template Selected State */
.template-selected {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.template-header {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%);
  border-radius: 12px;
  border: 2px solid #86efac;
}

.template-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.success-tag {
  font-size: 0.9rem;
}

.badge-text {
  font-weight: 700;
}

.change-template-btn {
  flex-shrink: 0;
}

.template-name-row {
  padding-top: 0.5rem;
  border-top: 2px solid #d1fae5;
}

.template-name {
  font-weight: 700;
  color: #166534;
  font-size: 1.35rem;
  letter-spacing: -0.02em;
  margin: 0;
  line-height: 1.3;
}

/* Message Preview Card */
.message-preview-card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border: 2px solid #e5e7eb;
}

.card-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.15rem;
  font-weight: 700;
  color: #111827;
}

.card-title i {
  color: #25d366;
  font-size: 1.25rem;
}

/* Preview Container */
.preview-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* WhatsApp Mockup */
.whatsapp-mockup {
  background: #e5ddd5;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.mockup-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  background: #075e54;
  color: white;
}

.mockup-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #128c7e;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

.mockup-info {
  flex: 1;
}

.mockup-name {
  font-weight: 600;
  font-size: 0.95rem;
}

.mockup-status {
  font-size: 0.75rem;
  opacity: 0.8;
}

.mockup-chat {
  padding: 1.5rem 1rem;
  min-height: 200px;
  background-image:
    linear-gradient(45deg, #dfd3c3 25%, transparent 25%),
    linear-gradient(-45deg, #dfd3c3 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #dfd3c3 75%),
    linear-gradient(-45deg, transparent 75%, #dfd3c3 75%);
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
}

/* WhatsApp-style Bubble */
.whatsapp-bubble {
  background: #ffffff;
  border-radius: 8px;
  padding: 10px 14px;
  position: relative;
  max-width: 85%;
  margin-left: auto;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
}

.whatsapp-bubble::before {
  content: '';
  position: absolute;
  right: -8px;
  top: 0;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0 0 12px 10px;
  border-color: transparent transparent transparent #ffffff;
}

.bubble-content {
  color: #303030;
  font-size: 0.95rem;
  line-height: 1.5;
  word-wrap: break-word;
  white-space: pre-wrap;
}

.bubble-time {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.7rem;
  color: #667781;
  margin-top: 0.35rem;
}

.bubble-time::after {
  content: '✓✓';
  color: #34b7f1;
  font-weight: bold;
  margin-left: 0.125rem;
}

.message-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: #f0f9ff;
  border-radius: 8px;
  border-left: 4px solid #3b82f6;
  color: #1e40af;
  font-size: 0.95rem;
}

.message-info i {
  color: #3b82f6;
  font-size: 1.25rem;
}

.message-info strong {
  color: #1e3a8a;
  font-weight: 700;
}

/* Variables Accordion */
.variables-accordion {
  margin-top: 0.5rem;
}

.accordion-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
}

.variables-content {
  padding: 1rem 0;
}

.variables-intro {
  margin: 0 0 1rem 0;
  color: var(--text-color);
}

/* Variables Grid */
.variables-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  margin-top: 1rem;
}

.variables-column {
  flex: 1 1 45%;
  min-width: 250px;
}

.variables-list {
  list-style: none;
  padding: 0;
  margin: 0.75rem 0;
}

.variables-list li {
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
}

.variables-list code {
  background-color: var(--surface-200);
  color: var(--primary-color);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
  font-weight: 600;
}

.example-text {
  margin: 0.75rem 0 0.5rem 0;
  padding: 0.75rem;
  background-color: var(--surface-100);
  border-left: 3px solid var(--primary-color);
  border-radius: 4px;
  font-size: 0.9rem;
}

.example-result {
  margin: 0.5rem 0 0 0;
  color: var(--text-color-secondary);
  font-size: 0.85rem;
}

/* Review Step */
.review-step {
  padding: 1rem 0;
}

/* Info Banner */
.info-banner {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.25rem;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-radius: 12px;
  border: 2px solid #93c5fd;
  margin-bottom: 1.5rem;
}

.info-banner-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #3b82f6;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.info-banner-content h4 {
  margin: 0 0 0.5rem 0;
  color: #1e40af;
  font-size: 1.1rem;
  font-weight: 700;
}

.info-banner-content p {
  margin: 0;
  color: #1e40af;
  font-size: 0.95rem;
  line-height: 1.5;
}

/* Review Table */
.review-table {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.review-table :deep(.p-datatable-thead > tr > th) {
  background: #f9fafb;
  color: #374151;
  font-weight: 700;
  font-size: 0.95rem;
  padding: 1rem;
  border-bottom: 2px solid #e5e7eb;
}

.review-table :deep(.p-datatable-tbody > tr > td) {
  padding: 1rem;
  vertical-align: middle;
}

.review-table :deep(.p-datatable-tbody > tr:hover) {
  background: #f9fafb;
}

/* Client Cell */
.client-cell {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.client-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  flex-shrink: 0;
}

/* Message Cell */
.message-cell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: background 0.2s ease;
}

.message-cell:hover {
  background: #f3f4f6;
}

.message-truncated {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #374151;
  font-size: 0.95rem;
}

.view-message-btn {
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.message-cell:hover .view-message-btn {
  opacity: 1;
}

/* Table Empty State */
.table-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
  color: #9ca3af;
}

.table-empty-state i {
  font-size: 4rem;
  opacity: 0.5;
  margin-bottom: 1rem;
}

.table-empty-state h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #6b7280;
  margin: 0 0 0.5rem 0;
}

.table-empty-state p {
  font-size: 0.95rem;
  color: #9ca3af;
  margin: 0;
}

/* Full Message Content */
.full-message-content {
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
  border-left: 4px solid #3b82f6;
  color: #374151;
  font-size: 0.95rem;
  line-height: 1.7;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 400px;
  overflow-y: auto;
}

/* Utility Classes */
.w-full {
  width: 100%;
}

/* Loading Container */
.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 2rem;
  font-size: 1.125rem;
  color: var(--text-color-secondary);
}

.pi-spin {
  animation: pi-spin 2s infinite linear;
}

@keyframes pi-spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(359deg);
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .step-content {
    padding: 1rem;
  }

  .empty-state-icon {
    font-size: 3rem;
  }

  .empty-state-title {
    font-size: 1.25rem;
  }

  .empty-state-description {
    font-size: 0.9rem;
  }

  .template-header {
    padding: 1.25rem;
    gap: 1rem;
  }

  .template-header-row {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }

  .change-template-btn {
    width: 100%;
  }

  .template-name {
    font-size: 1.15rem;
  }

  .mockup-chat {
    padding: 1rem 0.75rem;
    min-height: 150px;
  }

  .variables-grid {
    flex-direction: column;
    gap: 1.5rem;
  }

  .variables-column {
    flex: 1 1 100%;
  }

  .whatsapp-bubble {
    font-size: 0.9rem;
  }

  .info-banner {
    padding: 1rem;
  }

  .info-banner-icon {
    width: 40px;
    height: 40px;
    font-size: 1.25rem;
  }

  .info-banner-content h4 {
    font-size: 1rem;
  }

  .review-table :deep(.p-datatable-thead > tr > th) {
    font-size: 0.875rem;
    padding: 0.75rem;
  }

  .review-table :deep(.p-datatable-tbody > tr > td) {
    padding: 0.75rem;
  }

  .client-icon {
    width: 32px;
    height: 32px;
    font-size: 0.9rem;
  }

  .message-truncated {
    font-size: 0.875rem;
  }

  .full-message-content {
    font-size: 0.875rem;
  }
}
</style>
